import React, {useMemo, useState} from 'react'
import {toAbsoluteUrl} from '../../../../../../../_metronic/helpers'
import {EmptyState} from '../EmptyState'
import styles from '../../styles.module.css'

type Props = {
  plants?: any[]
  onSelectPlant?: (plant: any) => void
}

const PLANT_ICON = toAbsoluteUrl('/media/imgs/plant2.png')

const STATUS: Record<string, {label: string; className: string}> = {
  '0': {label: 'Online', className: 'bg-light-success text-success'},
  '1': {label: 'Offline', className: 'bg-light-danger text-danger'},
  '2': {label: 'Em alerta', className: 'bg-light-warning text-warning'},
  '3': {label: 'Informativo', className: 'bg-light-info text-info'},
  '7': {label: 'Informativo', className: 'bg-light-info text-info'},
}

const getStatus = (status: any) =>
  STATUS[status === null || status === undefined ? '' : String(status)] || {
    label: 'Sem status',
    className: 'bg-light text-gray-600',
  }

const toNumber = (value: any) => {
  const number = Number(String(value ?? '').replace(',', '.'))
  return Number.isFinite(number) ? number : 0
}

const formatNumber = (value: number, digits = 1) =>
  value.toLocaleString('pt-BR', {minimumFractionDigits: 0, maximumFractionDigits: digits})

const ListPlantsDetails: React.FC<Props> = ({plants = [], onSelectPlant}) => {
  const [search, setSearch] = useState('')

  const activePlants = useMemo(
    () => (plants || []).filter((plant: any) => !plant.deletePlant),
    [plants]
  )

  const stats = useMemo(
    () => ({
      total: activePlants.length,
      capacity: activePlants.reduce((sum, plant: any) => sum + toNumber(plant.capacity), 0),
      online: activePlants.filter((plant: any) => String(plant.status) === '0').length,
      attention: activePlants.filter((plant: any) => ['1', '2'].includes(String(plant.status)))
        .length,
    }),
    [activePlants]
  )

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return activePlants
    return activePlants.filter(
      (plant: any) =>
        (plant.name || '').toLowerCase().includes(term) ||
        (plant.address || '').toLowerCase().includes(term)
    )
  }, [activePlants, search])

  if (!activePlants.length) {
    return (
      <EmptyState
        icon='/media/icons/duotune/general/gen002.svg'
        title='Nenhuma usina vinculada'
        text='Edite o cliente para vincular as usinas que pertencem a ele.'
      />
    )
  }

  return (
    <>
      <div className={styles.stats}>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Usinas</span>
          <span className={styles.statValue}>{stats.total}</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Potência total</span>
          <span className={styles.statValue}>
            {formatNumber(stats.capacity, 2)}
            <span className={styles.statUnit}>kWp</span>
          </span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Online</span>
          <span className={`${styles.statValue} text-success`}>{stats.online}</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Precisam de atenção</span>
          <span className={`${styles.statValue} ${stats.attention ? 'text-danger' : ''}`}>
            {stats.attention}
          </span>
        </div>
      </div>

      {activePlants.length > 4 && (
        <div className={`input-group input-group-solid bg-body rounded ${styles.search}`}>
          <span className='input-group-text bg-body'>
            <i className='bi bi-search fs-6 text-gray-500'></i>
          </span>
          <input
            type='search'
            className='form-control form-control-solid bg-body'
            placeholder='Buscar usina por nome ou endereço'
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
      )}

      {filtered.length ? (
        <div className={styles.cardList}>
          {filtered.map((plant: any) => {
            const status = getStatus(plant.status)
            return (
              <button
                key={plant.id}
                type='button'
                className={`${styles.itemCard} ${styles.clickable}`}
                onClick={() => onSelectPlant?.(plant)}
                title='Abrir detalhes da usina'
              >
                <span className={styles.itemIcon}>
                  <img src={PLANT_ICON} alt='' />
                </span>
                <div className={styles.itemText}>
                  <span className={styles.itemTitle} title={plant.name}>
                    {plant.name || 'Usina sem nome'}
                  </span>
                  <span className={styles.itemSubtitle} title={plant.address}>
                    {plant.address || 'Endereço não localizado'}
                  </span>
                  <div className={styles.itemMeta}>
                    {plant.capacity ? (
                      <span>
                        <i className='bi bi-lightning-charge text-warning'></i>
                        {formatNumber(toNumber(plant.capacity), 2)} kWp
                      </span>
                    ) : null}
                    {plant.energyToday !== null && plant.energyToday !== undefined ? (
                      <span>
                        <i className='bi bi-sun text-primary'></i>
                        {formatNumber(toNumber(plant.energyToday))} kWh hoje
                      </span>
                    ) : null}
                  </div>
                </div>
                <span className={`${styles.statusBadge} ${status.className}`}>
                  <span className={styles.statusDot} />
                  {status.label}
                </span>
                <i className={`bi bi-chevron-right ${styles.chevron}`}></i>
              </button>
            )
          })}
        </div>
      ) : (
        <EmptyState
          icon='/media/icons/duotune/general/gen004.svg'
          title='Nenhuma usina encontrada'
          text='Tente buscar por outro nome ou endereço.'
        />
      )}
    </>
  )
}

export {ListPlantsDetails}
